import { apiClient } from './client';
import type {
  SaveWrongQuestionDto,
  SaveWrongQuestionFromPhotoSearchInput,
  WrongQuestionDto,
  WrongQuestionFilterViewModel,
  WrongQuestionSourceType,
  WrongQuestionViewModel,
  WrongQuestionsDto,
  WrongQuestionsViewModel,
} from '../types/wrongQuestions';
import { RUNTIME_API_CONFIG } from '../config/runtimeApi';

const WRONG_QUESTIONS_PATH = '/api/wrong-questions';
const ALL_FILTER_ID = 'all';

const fallbackWrongQuestionsDto: WrongQuestionDto[] = [
  {
    id: 'wrong-function-max',
    question: '二次函数顶点式应用',
    answer: '先化为顶点式，再结合开口方向判断最值。',
    steps: ['配方得到顶点坐标', '判断开口方向', '代入定义域端点校验'],
    subject: '数学',
    grade: '初二',
    chapter: '二次函数',
    knowledgePoint: '二次函数最值',
    errorTag: '审题遗漏定义域',
    customTags: ['函数', '拍照搜题'],
    masteryStatus: '未掌握',
    errorCount: 2,
    consecutiveCorrect: 0,
    createdAt: '今天',
    lastReviewedAt: null,
    reviewRound: 0,
    sourceExamId: null,
    sourceType: 'photo_search',
    sourceId: 'demo-photo-search-1',
    sourceQuestionNo: '1',
  },
  {
    id: 'wrong-buoyancy',
    question: '浮力与排开液体体积',
    answer: '使用阿基米德原理 F 浮 = rho 液 g V 排。',
    steps: ['确认液体密度', '计算排开体积', '代入公式'],
    subject: '物理',
    grade: '初二',
    chapter: '浮力',
    knowledgePoint: '浮力计算',
    errorTag: '单位换算',
    customTags: ['物理', '公式'],
    masteryStatus: '复习中',
    errorCount: 1,
    consecutiveCorrect: 1,
    createdAt: '昨天',
    lastReviewedAt: '昨天',
    reviewRound: 1,
    sourceExamId: null,
    sourceType: 'manual',
    sourceId: 'demo-manual-1',
    sourceQuestionNo: null,
  },
];

const sourceLabels: Record<string, string> = {
  photo_search: '拍照搜题',
  search_result: '搜题结果',
  correction_task: '批改任务',
  manual: '手动添加',
  practice_review: '复习练习',
};

const safeNumber = (value: number | null | undefined): number => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 0;
  }
  return value;
};

const formatDateLabel = (value: string | null | undefined): string => {
  if (!value) {
    return '暂无记录';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
  });
};

const mapFilter = (label: string): WrongQuestionFilterViewModel => ({
  id: label === '全部' ? ALL_FILTER_ID : label,
  label,
});

const getQuestionList = (dto: WrongQuestionsDto): WrongQuestionDto[] => {
  if (Array.isArray(dto)) {
    return dto;
  }
  return dto.questions ?? [];
};

const getFilterSubjects = (dto: WrongQuestionsDto, questions: WrongQuestionDto[]): string[] => {
  const explicitFilters = Array.isArray(dto) ? [] : dto.filters ?? [];
  const derivedSubjects = questions
    .map((question) => question.subject)
    .filter((subject): subject is string => Boolean(subject));

  return [...new Set([...explicitFilters, ...derivedSubjects])];
};

const mapWrongQuestionDtoToViewModel = (
  dto: WrongQuestionDto
): WrongQuestionViewModel => {
  const sourceType = dto.sourceType ?? null;
  return {
    id: dto.id,
    title: dto.question ?? dto.title ?? '未命名错题',
    subject: dto.subject ?? '综合',
    knowledgePoint: dto.knowledgePoint ?? dto.knowledge_point ?? '未标注知识点',
    retryCount: Math.max(0, Math.round(safeNumber(dto.errorCount ?? dto.retry_count))),
    lastWrongAt: formatDateLabel(dto.lastReviewedAt ?? dto.createdAt ?? dto.last_wrong_at),
    errorTag: dto.errorTag ?? '待复盘',
    errorType: dto.errorTag ?? '待复盘',
    masteryStatus: dto.masteryStatus ?? '未掌握',
    sourceType,
    sourceLabel: sourceType ? sourceLabels[sourceType] ?? sourceType : '未知来源',
  };
};

export const mapWrongQuestionsDtoToViewModel = (
  dto: WrongQuestionsDto
): WrongQuestionsViewModel => {
  const questions = getQuestionList(dto);
  const subjects = getFilterSubjects(dto, questions);
  return {
    filters: [mapFilter('全部'), ...subjects.map(mapFilter)],
    questions: questions.map(mapWrongQuestionDtoToViewModel),
  };
};

export const buildWrongQuestionFromPhotoSearchPayload = (
  input: SaveWrongQuestionFromPhotoSearchInput
): SaveWrongQuestionDto => ({
  question: input.question,
  answer: input.answer ?? '等待复习时补充答案',
  steps: input.steps ?? [],
  subject: input.subject ?? '综合',
  grade: input.grade ?? '未标注',
  chapter: input.chapter ?? null,
  knowledgePoint: input.knowledgePoint ?? '未标注知识点',
  errorTag: input.errorTag ?? '拍照搜题',
  customTags: input.customTags ?? ['拍照搜题'],
  masteryStatus: input.masteryStatus ?? '未掌握',
  errorCount: 1,
  consecutiveCorrect: 0,
  sourceExamId: null,
  sourceType: 'photo_search',
  sourceId: input.sourceId ?? input.problemId ?? input.imageRef ?? null,
  sourceQuestionNo: input.sourceQuestionNo ?? null,
});

export const getWrongQuestions = async (): Promise<WrongQuestionsViewModel> => {
  if (!RUNTIME_API_CONFIG.enabled) {
    return mapWrongQuestionsDtoToViewModel(fallbackWrongQuestionsDto);
  }

  const response = await apiClient.get<WrongQuestionsDto>(WRONG_QUESTIONS_PATH);
  return mapWrongQuestionsDtoToViewModel(response.data);
};

export const saveWrongQuestionFromPhotoSearch = async (
  input: SaveWrongQuestionFromPhotoSearchInput
): Promise<WrongQuestionViewModel> => {
  const payload = buildWrongQuestionFromPhotoSearchPayload(input);

  if (!RUNTIME_API_CONFIG.enabled) {
    return mapWrongQuestionDtoToViewModel({
      id: `local-photo-search-${Date.now()}`,
      ...payload,
      createdAt: '刚刚',
      lastReviewedAt: null,
      reviewRound: 0,
    });
  }

  const response = await apiClient.post<WrongQuestionDto>(WRONG_QUESTIONS_PATH, payload);
  return mapWrongQuestionDtoToViewModel(response.data);
};
